import { BottomBanner } from '@chiroup/components/BottomBanner';
import { createUSAePayMessageListener } from '@chiroup/core/functions/usaePayUtils';
import { useScript } from '@chiroup/hooks/useScript';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { LoadingOverlayProvider } from './components/common/LoadingOverlay';
import { KioskShell } from './components/kiosk/KioskEntry';
import PublicSchedule from './components/PublicSchedule';
import PublicRequest from './components/request/PublicRequest';
import RootLanding from './components/RootLanding';
import Survey from './components/Survey';
import SurveyUnsubscribe from './components/SurveyUnsubscribe';

const PAY_JS_URL = import.meta.env.VITE_PAY_JS_URL as string;

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const queryClient = new QueryClient();

function App() {
  const scriptStatus = useScript(PAY_JS_URL, {
    removeOnUnmount: false,
  });

  useEffect(() => {
    if (scriptStatus === 'ready') {
      // Monkey patch the payJS object to update the addEventListner function
      (window as any).usaepay.PaymentCard.prototype.addEventListener = function (
        eventType: any,
        response: any
      ) {
        const messageListener = createUSAePayMessageListener(eventType, response);

        // Add the event listener
        window.addEventListener('message', messageListener);

        // Cleanup: Remove the event listener when the component unmounts
        return function cleanup() {
          window.removeEventListener('message', messageListener);
        };
      };
    } else if (scriptStatus === 'error') {
      // Handle script loading error here if needed.
      console.error('Failed to load useepay script.');
    }
  }, [scriptStatus]);

  return (
    <QueryClientProvider client={queryClient}>
      <LoadingOverlayProvider>
        <BrowserRouter>
          <SentryRoutes>
            {/*
             * Public booking flow — schedule.healthcom.io/<clinicPath> in
             * the legacy world; reachable from the new domain at
             * /clinic-schedule/<clinicPath>. Bypasses the authed
             * RootLanding state machine so a brand-new visitor can pick
             * treatment / provider / time without being challenged for
             * a Cognito session up front. Auth happens at the confirm
             * step: existing patients sign in, new patients create a
             * pending account in the same form. DNS / routing layer
             * should rewrite schedule.healthcom.io/* → /clinic-schedule/*
             * so existing marketing links keep working.
             */}
            <Route path="/clinic-schedule/:clinicPath/*" element={<PublicSchedule />} />
            {/*
             * Public PatientRequest flow — moved over from healthcom-app.
             * Owns its own validationCode-based auth state machine
             * (RequestAuth → RequestCodeInput → RequestRunner) and
             * renders independently of the authed portal shell.
             */}
            <Route path="/requests/:id/*" element={<PublicRequest />} />
            {/*
             * Kiosk + Terminal — shared-device check-in flow. Auths via
             * a Request ID + verification code (no Cognito), then
             * mounts the runner with `kiosk={true}` so uploads are
             * filtered out and exit returns to the entry screen.
             */}
            <Route path="/kiosk/*" element={<KioskShell />} />
            <Route path="/terminal/*" element={<KioskShell />} />
            {/*
             * Post-care survey — fcid-tokenized URL emailed by the
             * 30-day cron. Must work for any visitor regardless of
             * portal/request/kiosk session, so it's mounted above the
             * RootLanding catch-all and posts to the legacy
             * `healthcom-api-noauth` /survey endpoint.
             */}
            {/*
             * Survey-email unsubscribe — linked from the review-link
             * and 30-day-survey SES templates (`/survey/unsubscribe?
             * token=…&lang=es?`). Public, no-auth; toggles
             * `Patient.optOutEmail` via the legacy
             * `/subscription-status` endpoints. Listed before `/survey`
             * since React Router v6 matches exact paths, but kept
             * adjacent so the survey-related routes stay together.
             */}
            <Route path="/survey/unsubscribe" element={<SurveyUnsubscribe />} />
            <Route path="/survey" element={<Survey />} />
            {/*
             * Everything else goes through RootLanding. It handles login,
             * clinic picking, and (once authed with a clinic) renders
             * Container inline so the URL stays wherever the user is.
             */}
            <Route path="/*" element={<RootLanding />} />
          </SentryRoutes>
        </BrowserRouter>
        {import.meta.env.VITE_STAGE === 'sandbox' && (
          <BottomBanner message="You are in sandbox mode. Do not use real data." />
        )}
      </LoadingOverlayProvider>
    </QueryClientProvider>
  );
}

export default App;
